import Tippy from '@tippyjs/react';

import { Truncate, formatDate, mString } from 'lib/helpers/utility';

import {
  calcInvoiceDiscountFromProducts,
  calculateInvoiceTaxFromItems
} from 'components/practice/NewAppointment/components/Sections/Pricing/components/ProceduresProduct/components/SelectedItems/helpers';

const handleEstLabel = ({ amount, isEstimated, label }) => {
  const finalLabel = label || mString(amount || 0);

  if (isEstimated && amount > 0) {
    return `estimated ${finalLabel}`;
  }

  return finalLabel;
};

const DETAILS = {
  field: 'details',
  cellClass: 'no-border',
  cellRenderer: 'agGroupCellRenderer',
  minWidth: 100,
  showRowGroup: true,
  suppressMenu: true,
  tooltipField: 'details',
  flex: 1
};

const getDosCol = (practiceTimezone) => ({
  field: 'dateofservice',
  headerName: 'DOS',
  valueFormatter: ({ value }) => formatDate(value, practiceTimezone, true),
  maxWidth: 130,
  cellClass: 'no-border',
  flex: 1
});

const TYPE = { field: 'type', cellClass: 'no-border', flex: 1 };

const QTY = {
  field: 'qty',
  maxWidth: 80,
  minWidth: 70,
  cellClass: 'no-border',
  flex: 1
};

const CHARGE = {
  field: 'charge',
  valueFormatter: ({ value }) => mString(value || 0),
  cellClass: 'no-border',
  flex: 1
};

const INS_PAYMENT = {
  field: 'ins_payment',
  headerName: 'Ins Pay',
  valueGetter: (params) => {
    const amount = params?.data?.ins_payment;
    const isEstimated = params?.data?.isEstimated;

    return handleEstLabel({ amount, isEstimated });
  },
  cellClass: 'no-border',
  flex: 1
};

const OTHER_ADJ = {
  field: 'other_adj',
  headerName: 'Other Adj',
  valueFormatter: ({ value }) => mString(value || 0),
  cellClass: 'no-border',
  flex: 1
};

const INS_BALANCE = {
  field: 'ins_balance',
  headerName: 'Ins Balance',
  valueFormatter: ({ value }) => mString(value || 0),
  sortable: false,
  cellClass: 'no-border',
  flex: 1
};

const PT_BALANCE = {
  field: 'ptBalance',
  headerName: 'Pt Balance',
  valueFormatter: ({ value }) => mString(value || 0),
  maxWidth: 120,
  cellClass: 'no-border',
  flex: 1
};

export const PARENT_COLUMN_DEFS = (practiceTimezone, isAEOB, isDetailed, claims) =>
  [
    DETAILS,
    {
      headerName: 'CPT® Code',
      field: 'procedure_code',
      minWidth: 100,
      flex: 1
    },
    getDosCol(practiceTimezone),
    isAEOB && TYPE,
    isDetailed && QTY,
    CHARGE,
    isDetailed && {
      field: 'discount',
      flex: 1,
      valueGetter: (params) => {
        const { type, discount, products, procedures } = params?.data || {};

        let discountValue = discount?.amount_cents || 0;

        if (type === 'Package' && (products?.length || procedures?.length)) {
          const invoiceDiscount = calcInvoiceDiscountFromProducts({ products, procedures });
          discountValue = invoiceDiscount?.amount_cents || 0;
        }

        return mString(discountValue);
      },
      cellClass: 'no-border'
    },
    isDetailed && {
      headerName: 'Tax',
      field: 'tax',
      cellClass: 'no-border',
      flex: 1,
      valueGetter: (params) => {
        const taxAmount = calculateInvoiceTaxFromItems({ mergedItems: [params?.data] });

        return mString(taxAmount);
      }
    },
    isAEOB && claims && INS_PAYMENT,
    isAEOB &&
      claims && {
        field: 'ins_adjustment',
        headerName: 'Ins Adj',
        valueGetter: (params) => {
          const amount = params?.data?.ins_adjustment;
          const isEstimated = params?.data?.isEstimated;
          const finalInsAdj = amount != null ? mString(amount || 0) : '-';

          return handleEstLabel({ amount, isEstimated, label: finalInsAdj });
        },
        cellClass: 'no-border',
        flex: 1
      },
    isDetailed && OTHER_ADJ,
    isAEOB && INS_BALANCE,
    PT_BALANCE,
    isDetailed && {
      field: 'note',
      headerName: 'Note',
      maxWidth: 220,
      cellClass: 'no-border',
      flex: 1,
      valueGetter: ({ data }) => data?.note || '-',
      cellRenderer: ({ value }) => (
        <Tippy
          className="tippy-dark"
          content={value}
          placement={value.length > 400 ? 'left' : 'top'}>
          <div>{Truncate(value, 10)}</div>
        </Tippy>
      )
    }
  ].filter(Boolean);

export const CHILDREN_COLUMN_DEFS = (practiceTimezone, isAEOB, isDetailed, claims) =>
  [
    DETAILS,
    getDosCol(practiceTimezone),
    isAEOB && TYPE,
    isDetailed && QTY,
    CHARGE,
    isAEOB && claims && INS_PAYMENT,
    isAEOB &&
      claims && {
        field: 'ins_adjustment',
        headerName: 'Ins Adj',
        valueGetter: (params) => {
          const amount = params?.data?.ins_adjustment;
          const isEstimated = params?.data?.isEstimated;

          return handleEstLabel({ amount, isEstimated });
        },
        cellClass: 'no-border',
        flex: 1
      },
    isDetailed && OTHER_ADJ,
    isDetailed && INS_BALANCE,
    PT_BALANCE
  ].filter(Boolean);
